/**
 * Manage Campaign accordion for Meta campaigns.
 *
 * Mirrors Google's `EntityCardList` shape: one expandable card per ad
 * set, an inline tab bar inside each expanded card. Card headers and
 * row chrome are structure-only — NO status badge and NO inline kebab.
 * Status (Active / Paused / Archived) and per-entity actions (pause /
 * enable / archive) live in the Campaign Structure tree
 * (`MetaCampaignElementsList`), same split Google uses between
 * `EntityCard` and `CampaignElementsList`. Campaign-level write
 * actions (Pause / Edit Name / Archive on the campaign itself) live
 * on the account-page campaign card, same as Google.
 *
 * Tabs (analog mapping vs Google's `getAvailableTabs`):
 * - `ads`       — list of ads under the ad set (≈ Google RSAs / RDAs / Demand Gen Ads)
 * - `targeting` — structured audience / location / placement summary (≈ Google Audiences)
 *
 * Meta has no analog for Keywords / Negative Keywords / Search Terms /
 * Channels, so those tabs are absent by design. A `creatives` tab is
 * deferred per the `meta-a3-creatives-deferred` memory — the §9.0.1
 * Media-Library-as-creative-substrate decision gates A4.
 *
 * Performance metrics (impressions / clicks / spend) live in the
 * Performance Metrics card, not here. This card is for structure only
 * in A3.5; A4 adds inline edit affordances per panel.
 */
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Campaign } from '@/types/campaign';
import type { MetaAdSet, MetaAd } from '@/types/metaCampaign';
import { MetaAdEditor } from '@/components/features/MetaAdCreation/MetaAdEditor';

type MetaAdSetTab = 'ads' | 'targeting';

interface EditingAd {
  adId: string;
  adSetId: string;
  adSetName: string | null;
}

const TAB_LABELS: Record<MetaAdSetTab, string> = {
  ads: 'Ads',
  targeting: 'Targeting',
};

interface MetaManagementAccordionProps {
  campaign: Campaign;
}

export function MetaManagementAccordion({ campaign }: MetaManagementAccordionProps) {
  const adSets = campaign.ad_sets ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(
    adSets.length === 1 ? adSets[0].ad_set_id : null,
  );
  const [editingAd, setEditingAd] = useState<EditingAd | null>(null);
  const queryClient = useQueryClient();

  // `meta_ad_account_id` is populated on Meta-source campaigns. Absent
  // on Google-source campaigns — but this accordion only renders for
  // Meta campaigns (parent branch decides), so an absent value here is
  // an unexpected data anomaly. Guard the editor open on presence so
  // we surface it as a click-does-nothing rather than a broken PATCH.
  const adAccountId = campaign.meta_ad_account_id ?? null;

  const handleEditorSaved = React.useCallback(() => {
    setEditingAd(null);
    // Invalidate the ad-set / campaign detail caches so the ad list
    // reflects the new state on next render.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache'] });
    queryClient.invalidateQueries({ queryKey: ['app-config'] });
  }, [queryClient]);
  const handleEditorCancel = React.useCallback(() => {
    setEditingAd(null);
  }, []);

  if (!adSets.length) {
    return (
      <div className="p-6 bg-yellow-50 border-t border-yellow-200">
        <p className="text-yellow-800">
          No ad sets found for this Meta campaign.
        </p>
      </div>
    );
  }

  // Layout mirrors Google's `EntityCardList`: a labeled section bar
  // followed by a padded list of bordered per-entity cards with
  // spacing between them. Replaces the previous flat
  // `divide-y`-only stack so each ad set reads as a distinct,
  // chevron-headed card like Google's ad groups.
  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">
          Ad Sets ({adSets.length})
        </h3>
      </div>
      <div className="p-3 pb-4 space-y-2">
        {adSets.map((adSet) => (
          <MetaAdSetCard
            key={adSet.ad_set_id}
            adSet={adSet}
            adAccountId={adAccountId}
            isExpanded={expandedId === adSet.ad_set_id}
            onToggle={() =>
              setExpandedId((prev) => (prev === adSet.ad_set_id ? null : adSet.ad_set_id))
            }
            editingAd={
              editingAd && editingAd.adSetId === adSet.ad_set_id
                ? editingAd
                : null
            }
            onEditAd={
              adAccountId
                ? (adId) =>
                    setEditingAd({
                      adId,
                      adSetId: adSet.ad_set_id,
                      adSetName: adSet.ad_set_name ?? null,
                    })
                : null
            }
            onEditorSaved={handleEditorSaved}
            onEditorCancel={handleEditorCancel}
          />
        ))}
      </div>
    </div>
  );
}

interface MetaAdSetCardProps {
  adSet: MetaAdSet;
  /** The parent Meta campaign's `meta_ad_account_id`. Non-null when
   *  the ad editor can be opened; null makes clicks inert. */
  adAccountId: string | null;
  isExpanded: boolean;
  onToggle: () => void;
  /** The editingAd tuple when it targets an ad in THIS ad set — else
   *  null. When set, the Ads tab panel renders `MetaAdEditor` inline
   *  (parity with Google's EntityCard `isEditingRSAForThisEntity`). */
  editingAd: EditingAd | null;
  /** Non-null when the parent Meta campaign has an `ad_account_id` and
   *  the ad editor can be opened. Null disables click-to-edit — the
   *  ad list still renders, but clicking a row is a no-op. */
  onEditAd: ((adId: string) => void) | null;
  onEditorSaved: () => void;
  onEditorCancel: () => void;
}

function MetaAdSetCard({
  adSet,
  adAccountId,
  isExpanded,
  onToggle,
  editingAd,
  onEditAd,
  onEditorSaved,
  onEditorCancel,
}: MetaAdSetCardProps) {
  const [activeTab, setActiveTab] = useState<MetaAdSetTab>('ads');
  const ads = adSet.ads ?? [];
  const adCount = ads.length;
  const tabCounts: Record<MetaAdSetTab, number | undefined> = {
    ads: adCount,
    targeting: undefined,
  };

  // Card chrome and header layout mirror Google's `EntityCard`
  // (`pages/Campaign/components/CampaignManagement/EntityCard.tsx`):
  // bordered rounded box, chevron + name row, indented summary
  // below, expanded zone with grey background carrying the tab bar
  // and panel content.
  return (
    <div
      className={`
        border rounded-lg transition-shadow
        ${isExpanded ? 'shadow-md border-gray-300' : 'shadow-sm border-gray-200 hover:shadow'}
      `}
    >
      {/* Header: chevron + name + indented summary only. Status badge
          and per-ad-set kebab moved to the Campaign Structure tree
          (`MetaCampaignElementsList`) so the structure surface owns all
          status state + actions — matches Google's EntityCard / Search
          campaigns layout, where the ad-group header carries no badge
          or kebab either. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isExpanded}
        className={`
          flex flex-col px-4 py-3 cursor-pointer rounded-t-lg
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
          ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50 rounded-b-lg'}
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {adSet.ad_set_name ?? adSet.ad_set_id}
          </h3>
        </div>
        {/* Indented summary — same `ml-7` offset as Google's EntityCard so
            it lines up under the name (past the chevron). */}
        <p className="mt-0.5 ml-7 text-xs text-gray-500">
          {adCount} ad{adCount === 1 ? '' : 's'}
        </p>
      </div>

      {isExpanded && (
        <div>
          <MetaAdSetTabBar
            adSetId={adSet.ad_set_id}
            activeTab={activeTab}
            onChange={setActiveTab}
            counts={tabCounts}
          />
          {/* `role="tabpanel"` with `aria-labelledby` pointing at the
              active tab's id completes the WAI-ARIA tabs pattern. The
              `id` here is the `aria-controls` target on the tab
              button — keep both sides in sync if the id format
              changes.

              No padding on the tabpanel itself — each child panel owns
              its own `p-4` the way Google's `EntityCard` lets its
              child panels (RSAPanel, KeywordsPanel, etc.) control
              their inset. That keeps the panel chrome consistent
              across Google + Meta and avoids the double-pad we'd get
              if both the wrapper and the child added padding. */}
          <div
            role="tabpanel"
            id={`meta-tabpanel-${adSet.ad_set_id}-${activeTab}`}
            aria-labelledby={`meta-tab-${adSet.ad_set_id}-${activeTab}`}
            className="bg-white rounded-b-lg"
          >
            {activeTab === 'ads' && (
              // Editor swap parity with Google's `EntityCard.tsx:195` —
              // when an ad in THIS ad set is being edited, replace the
              // AdsTab list content with `MetaAdEditor` inline. The
              // ad-set card + tab bar remain visible above so users can
              // switch tabs or collapse the ad set without closing the
              // editor. Cancel/Save returns to the ads list.
              editingAd && adAccountId ? (
                <div className="p-4">
                  <MetaAdEditor
                    adAccountId={adAccountId}
                    adId={editingAd.adId}
                    adSetName={editingAd.adSetName}
                    onSaved={onEditorSaved}
                    onClose={onEditorCancel}
                  />
                </div>
              ) : (
                <AdsTab ads={ads} onEditAd={onEditAd} />
              )
            )}
            {activeTab === 'targeting' && <TargetingTab targeting={adSet.targeting ?? {}} />}
          </div>
        </div>
      )}
    </div>
  );
}

interface MetaAdSetTabBarProps {
  adSetId: string;
  activeTab: MetaAdSetTab;
  onChange: (tab: MetaAdSetTab) => void;
  counts: Record<MetaAdSetTab, number | undefined>;
}

function MetaAdSetTabBar({ adSetId, activeTab, onChange, counts }: MetaAdSetTabBarProps) {
  const tabs: MetaAdSetTab[] = ['targeting', 'ads'];
  // Tab bar styling mirrors Google's `EntityTabBar`
  // (`pages/Campaign/components/CampaignManagement/EntityTabBar.tsx`):
  // a `bg-gray-50` strip continuing the gray header above, no inter-tab
  // gap, `px-4 py-2.5` button padding so the labels sit at the same
  // 16px inset as the panel content below, and the active tab raised
  // out of the gray strip via `bg-white`. Same WAI-ARIA tabs pattern
  // (tablist on the nav, tab + aria-controls on each button, panel
  // back-references via aria-labelledby).
  return (
    <div className="border-b border-gray-200 bg-gray-50">
      <nav
        role="tablist"
        className="flex -mb-px"
        aria-label="Ad set sections"
      >
        {tabs.map((tab) => {
          const count = counts[tab];
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => onChange(tab)}
              role="tab"
              id={`meta-tab-${adSetId}-${tab}`}
              aria-controls={`meta-tabpanel-${adSetId}-${tab}`}
              aria-selected={isActive}
              className={`
                px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                ${isActive
                  ? 'border-blue-600 text-blue-600 bg-white'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {TAB_LABELS[tab]}
              {typeof count === 'number' && count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                  isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function AdsTab({
  ads,
  onEditAd,
}: {
  ads: MetaAd[];
  onEditAd: ((adId: string) => void) | null;
}) {
  // Card chrome mirrors Google's `RSAPanel` (plain bordered card, gray
  // hover, "(Paused)" prefix on the type label, text "Edit" link on
  // the right). Clicking a row opens `MetaAdEditor` (the A4.x Meta ad
  // editor) via the injected `onEditAd` callback; when null (parent
  // campaign missing `meta_ad_account_id`) the row is inert. Per-ad
  // status changes (pause / enable / archive) are NOT surfaced here —
  // they live in the Campaign Structure tree's
  // `MetaCampaignElementsList` kebab (single source of truth for ad-
  // and ad-set-level status writes). The editor can still change
  // status too; that's a deliberate secondary path.
  const handleEditAd = (adId: string) => {
    if (onEditAd) onEditAd(adId);
  };

  if (!ads.length) {
    return (
      <div className="p-4">
        <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">No ads in this ad set</p>
          <p className="text-sm text-gray-500 mt-1">
            Create ads in Meta Ads Manager to manage them here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-3">
        Click on a Meta ad to edit its creative and copy
      </p>

      <div className="space-y-2">
        {ads.map((ad) => {
          const status = (ad.status ?? '').toUpperCase();
          const isPaused = status === 'PAUSED';
          const isArchived = status === 'ARCHIVED' || status === 'DELETED';
          const onClick = () => handleEditAd(ad.ad_id);
          return (
            <div
              key={ad.ad_id}
              role="button"
              tabIndex={0}
              aria-label={`Edit Meta ad: ${ad.ad_name ?? ad.ad_id}`}
              className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={onClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                  <span className={`text-xs font-medium flex-shrink-0 ${
                    isArchived
                      ? 'text-red-600'
                      : isPaused
                        ? 'text-yellow-600'
                        : 'text-blue-600'
                  }`}>
                    Ad{isPaused ? ' (Paused)' : isArchived ? ' (Archived)' : ''}:
                  </span>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {ad.ad_name ?? ad.ad_id}
                  </p>
                </div>
                <span
                  className="text-xs text-blue-600 hover:text-blue-800 flex-shrink-0"
                  aria-hidden="true"
                >
                  Edit
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Targeting renderer ──────────────────────────────────────────────────
//
// Meta's `targeting` JSONB is platform-defined and varies by campaign type.
// The mapping below covers the fields that appear on a typical Meta ad set
// (audience, geo, placement). Anything not mapped is summarized as
// "N additional targeting rules" so a debug pane is never the primary UX.

const GENDER_LABELS: Record<number, string> = { 1: 'Male', 2: 'Female' };
const KNOWN_KEYS = new Set([
  'age_min', 'age_max', 'genders', 'geo_locations', 'excluded_geo_locations',
  'interests', 'behaviors', 'custom_audiences', 'excluded_custom_audiences',
  'publisher_platforms', 'facebook_positions', 'instagram_positions',
  'audience_network_positions', 'messenger_positions',
  'device_platforms', 'locales', 'flexible_spec',
]);

interface NamedEntry { id?: string | number; key?: string; name?: string }

function pickNames(entries: unknown): string[] {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((e) => {
      if (typeof e === 'string') return e;
      if (e && typeof e === 'object') {
        const n = (e as NamedEntry).name;
        if (typeof n === 'string' && n) return n;
        const k = (e as NamedEntry).key;
        if (typeof k === 'string' && k) return k;
      }
      return null;
    })
    .filter((n): n is string => !!n);
}

function renderAgeRange(targeting: Record<string, unknown>): string | null {
  const min = typeof targeting.age_min === 'number' ? targeting.age_min : null;
  const max = typeof targeting.age_max === 'number' ? targeting.age_max : null;
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}–${max}`;
  if (min != null) return `${min}+`;
  return `up to ${max}`;
}

function renderGenders(targeting: Record<string, unknown>): string | null {
  const g = targeting.genders;
  if (!Array.isArray(g) || g.length === 0) return null;
  const labels = g.map((n) => (typeof n === 'number' ? GENDER_LABELS[n] : null)).filter(Boolean);
  if (!labels.length) return null;
  if (labels.length === 2 && labels.includes('Male') && labels.includes('Female')) return 'All';
  return labels.join(', ');
}

function renderLocations(geo: unknown): string | null {
  if (!geo || typeof geo !== 'object') return null;
  const g = geo as Record<string, unknown>;
  const parts: string[] = [];
  const countries = Array.isArray(g.countries) ? g.countries.filter((c) => typeof c === 'string') : [];
  if (countries.length) parts.push(countries.join(', '));
  const regionNames = pickNames(g.regions);
  if (regionNames.length) parts.push(regionNames.join(', '));
  const cityNames = pickNames(g.cities);
  if (cityNames.length) parts.push(cityNames.join(', '));
  return parts.length ? parts.join(' · ') : null;
}

interface TargetingRowProps { label: string; value: React.ReactNode }
function TargetingRow({ label, value }: TargetingRowProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b border-gray-100 last:border-b-0">
      <dt className="text-xs font-medium text-gray-600 uppercase tracking-wider">{label}</dt>
      <dd className="md:col-span-2 text-sm text-gray-900 break-words">{value}</dd>
    </div>
  );
}

function TargetingTab({ targeting }: { targeting: Record<string, unknown> }) {
  const keys = Object.keys(targeting);
  if (!keys.length) {
    return (
      <div className="p-4">
        <p className="text-xs text-gray-500 italic">
          No targeting spec available for this ad set.
        </p>
      </div>
    );
  }

  const rows: Array<{ label: string; value: React.ReactNode }> = [];

  const age = renderAgeRange(targeting);
  if (age) rows.push({ label: 'Age', value: age });

  const genders = renderGenders(targeting);
  if (genders) rows.push({ label: 'Genders', value: genders });

  const geo = renderLocations(targeting.geo_locations);
  if (geo) rows.push({ label: 'Locations', value: geo });

  const excludedGeo = renderLocations(targeting.excluded_geo_locations);
  if (excludedGeo) rows.push({ label: 'Excluded Locations', value: excludedGeo });

  const interests = pickNames(targeting.interests);
  if (interests.length) rows.push({ label: 'Interests', value: interests.join(', ') });

  const behaviors = pickNames(targeting.behaviors);
  if (behaviors.length) rows.push({ label: 'Behaviors', value: behaviors.join(', ') });

  const customAuds = pickNames(targeting.custom_audiences);
  if (customAuds.length) rows.push({ label: 'Custom Audiences', value: customAuds.join(', ') });

  const excludedAuds = pickNames(targeting.excluded_custom_audiences);
  if (excludedAuds.length) rows.push({ label: 'Excluded Audiences', value: excludedAuds.join(', ') });

  if (Array.isArray(targeting.publisher_platforms)) {
    rows.push({ label: 'Platforms', value: (targeting.publisher_platforms as string[]).join(', ') });
  }

  // Positions are per-platform — surface each non-empty list with a clear
  // label so it's obvious which platform a placement applies to.
  const positionFields: Array<[string, string]> = [
    ['facebook_positions', 'Facebook Placements'],
    ['instagram_positions', 'Instagram Placements'],
    ['audience_network_positions', 'Audience Network Placements'],
    ['messenger_positions', 'Messenger Placements'],
  ];
  for (const [key, label] of positionFields) {
    const v = targeting[key];
    if (Array.isArray(v) && v.length) {
      rows.push({ label, value: (v as string[]).join(', ') });
    }
  }

  if (Array.isArray(targeting.device_platforms) && targeting.device_platforms.length) {
    rows.push({ label: 'Devices', value: (targeting.device_platforms as string[]).join(', ') });
  }

  if (Array.isArray(targeting.locales) && targeting.locales.length) {
    // `locales` is a list of Meta locale IDs (ints). Without a lookup
    // table we surface raw values — A4 can wire the locale-name lookup
    // when the audience builder lands.
    rows.push({ label: 'Locales', value: (targeting.locales as Array<string | number>).join(', ') });
  }

  // Anything we didn't recognize → soft-flag as a count rather than dumping.
  const unmapped = keys.filter((k) => !KNOWN_KEYS.has(k));
  const hasFlexibleSpec = Array.isArray(targeting.flexible_spec) && (targeting.flexible_spec as unknown[]).length > 0;
  const extraCount = unmapped.length + (hasFlexibleSpec ? 1 : 0);

  if (!rows.length && extraCount === 0) {
    return (
      <div className="p-4">
        <p className="text-xs text-gray-500 italic">
          Targeting is present but uses fields not yet rendered. Editor lands in a follow-up release.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <dl className="bg-white border border-gray-200 rounded-md px-4">
        {rows.map((r) => (
          <TargetingRow key={r.label} label={r.label} value={r.value} />
        ))}
      </dl>
      {extraCount > 0 && (
        <p className="text-xs text-gray-500 italic mt-2">
          {extraCount} additional targeting rule{extraCount === 1 ? '' : 's'} not shown.
        </p>
      )}
    </div>
  );
}
